'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Bell, BellOff, CheckCircle2, X, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

import { useAuth } from '@/providers/AuthProvider';
import { useNotification } from '@/providers/NotificationProvider';
import { getCurrentConfig } from '@/lib/api-adapter/config';

/* -------------------------------------------------------------------------- */
/*                               LocalStorage                                 */
/* -------------------------------------------------------------------------- */

const getStorageKey = () => {
  try {
    return `stock_alerts_${getCurrentConfig()?.storeId || 'default'}`;
  } catch {
    return `stock_alerts_default`;
  }
};

const getSubscribedAlerts = (): Record<string, boolean> => {
  if (typeof window === 'undefined') return {};
  try {
    return JSON.parse(localStorage.getItem(getStorageKey()) || '{}');
  } catch {
    return {};
  }
};

const setSubscribedAlert = (key: string) => {
  const alerts = getSubscribedAlerts();
  alerts[key] = true;
  localStorage.setItem(getStorageKey(), JSON.stringify(alerts));
};

const removeSubscribedAlert = (key: string) => {
  const alerts = getSubscribedAlerts();
  delete alerts[key];
  localStorage.setItem(getStorageKey(), JSON.stringify(alerts));
};

/* -------------------------------------------------------------------------- */
/*                               Guest Email                                  */
/* -------------------------------------------------------------------------- */

const guestKey = (storeId: string) => `stock_alert_emails_${storeId}`;

const getGuestEmails = (): Record<string, string> => {
  try {
    const storeId = getCurrentConfig()?.storeId || 'default';
    return JSON.parse(localStorage.getItem(guestKey(storeId)) || '{}');
  } catch {
    return {};
  }
};

const saveGuestEmail = (alertKey: string, email: string) => {
  const storeId = getCurrentConfig()?.storeId || 'default';
  const emails = getGuestEmails();
  emails[alertKey] = email;
  localStorage.setItem(guestKey(storeId), JSON.stringify(emails));
};

const removeGuestEmail = (alertKey: string) => {
  const storeId = getCurrentConfig()?.storeId || 'default';
  const emails = getGuestEmails();
  delete emails[alertKey];
  localStorage.setItem(guestKey(storeId), JSON.stringify(emails));
};

const getLastGuestEmail = () => {
  const values = Object.values(getGuestEmails());
  return values.length ? values[values.length - 1] : '';
};

/* -------------------------------------------------------------------------- */
/*                                Types                                       */
/* -------------------------------------------------------------------------- */

interface NotifyWhenInStockButtonProps {
  productId: string;
  variantId: string;
  className?: string;
  size?: 'sm' | 'md';
  appearance?: 'default' | 'compact';
}

/* -------------------------------------------------------------------------- */
/*                                Component                                   */
/* -------------------------------------------------------------------------- */

export function NotifyWhenInStockButton({
  productId,
  variantId,
  className = '',
  size = 'md',
  appearance = 'default',
}: NotifyWhenInStockButtonProps) {
  const { isAuthenticated, user } = useAuth();
  const notification = useNotification();

  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [email, setEmail] = useState('');

  const alertKey = `${productId}_${variantId}`;
  const isSmall = size === 'sm';
  const isCompact = appearance === 'compact';

  /* -------------------------------------------------------------------------- */
  /*                                 Init                                       */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    setIsSubscribed(!!getSubscribedAlerts()[alertKey]);
  }, [alertKey]);

  /* -------------------------------------------------------------------------- */
  /*                              API functions                                 */
  /* -------------------------------------------------------------------------- */

  const subscribe = useCallback(
    async (guestEmail?: string) => {
      try {
        setLoading(true);

        const config = getCurrentConfig();

        await axios.post(
          `${config.apiBaseUrl}/products/${productId}/variants/${variantId}/stock-alert`,
          guestEmail ? { email: guestEmail } : undefined,
        );

        setSubscribedAlert(alertKey);

        if (guestEmail) saveGuestEmail(alertKey, guestEmail);

        setIsSubscribed(true);
        setShowEmail(false);
        setEmail('');

        notification.success(
          "You'll be notified",
          'We will email you once this item is back in stock.',
        );
      } catch (err: any) {
        const status = err?.response?.status;

        if (status === 409) {
          setIsSubscribed(true);
        } else {
          notification.error(
            'Subscription failed',
            'Unable to set stock alert. Please try again.',
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [productId, variantId, alertKey, notification],
  );

  const unsubscribe = useCallback(async () => {
    try {
      setLoading(true);

      const config = getCurrentConfig();

      await axios.delete(
        `${config.apiBaseUrl}/products/${productId}/variants/${variantId}/stock-alert`,
        {
          data: isAuthenticated
            ? { userId: user?._id || user?.id }
            : { email: getGuestEmails()[alertKey] },
        },
      );

      removeSubscribedAlert(alertKey);
      removeGuestEmail(alertKey);

      setIsSubscribed(false);

      notification.success('Alert removed', 'You will no longer receive alerts.');
    } catch {
      notification.error('Error', 'Failed to remove alert.');
    } finally {
      setLoading(false);
    }
  }, [productId, variantId, isAuthenticated, user, alertKey, notification]);

  /* -------------------------------------------------------------------------- */
  /*                                Handlers                                    */
  /* -------------------------------------------------------------------------- */

  const handleSubscribeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isAuthenticated) subscribe();
    else {
      setEmail(getLastGuestEmail());
      setShowEmail(true);
    }
  };

  const handleGuestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await subscribe(email);
  };

  /* -------------------------------------------------------------------------- */
  /*                                Styles                                      */
  /* -------------------------------------------------------------------------- */

const baseBtn =
  'relative inline-flex items-center justify-center gap-2 rounded-full font-semibold tracking-wide select-none ' +
  'transition-all duration-300 ease-out active:scale-[0.96] ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-white';
const widthStyle = isCompact ? 'w-auto shrink-0' : 'w-full';
const subscribeStyle =
  'border-2 border-hsecondary/30 bg-hsecondary/10 text-hsecondary ' +
  'shadow-sm hover:bg-hsecondary/50 hover:shadow-md ' +
  'focus-visible:ring-hsecondary disabled:cursor-not-allowed disabled:opacity-50 ' +
  'disabled:hover:border-hsecondary/30 disabled:hover:bg-hsecondary/10 ' +
  'disabled:hover:text-hsecondary disabled:hover:shadow-none';
const subscribedStyle =
  'group/notify relative bg-emerald-50/70 text-emerald-700 border border-emerald-200 ' +
  'shadow-sm backdrop-blur-sm ' +
  'hover:bg-red-50 hover:text-red-600 hover:border-red-200 hover:shadow-md ' +
  'transition-all duration-200 ease-out ' +
  'focus-visible:ring-emerald-500';
  /* -------------------------------------------------------------------------- */
  /*                              Subscribed UI                                 */
  /* -------------------------------------------------------------------------- */

  if (isSubscribed) {
    if (!isAuthenticated) {
      return (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          className={`inline-flex items-center justify-center gap-2 rounded-full border border-emerald-200 bg-emerald-50/70 text-xs font-semibold text-emerald-700 ${widthStyle} ${isCompact ? 'px-4 py-2' : 'px-6 py-3'} ${className}`}
        >
          <CheckCircle2 className="h-4 w-4" />
          Alert active
        </motion.div>
      );
    }

    return (
      <motion.button
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        whileTap={{ scale: 0.97 }}
        disabled={loading}
        onClick={e => { e.preventDefault(); e.stopPropagation(); unsubscribe(); }}
        title="Click to remove alert"
        className={`${baseBtn} ${subscribedStyle} ${widthStyle} ${isCompact ? 'px-4 py-2' : 'px-6 py-3'} text-xs ${className}`}
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <>
            <span className="group-hover/notify:hidden flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Alert active
            </span>
            <span className="hidden group-hover/notify:flex items-center gap-2">
              <BellOff className="h-4 w-4" />
              Remove alert
            </span>
          </>
        )}
      </motion.button>
    );
  }

  /* -------------------------------------------------------------------------- */
  /*                               Email Form                                   */
  /* -------------------------------------------------------------------------- */

  if (showEmail) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 6 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        className={`${isCompact ? 'w-72 max-w-[65vw]' : 'w-full'} space-y-3 rounded-2xl border border-hsecondary/20 bg-white/95 p-4 shadow-lg`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-hsecondary/15">
              <Bell className="h-4 w-4 text-hsecondary" />
            </span>
            <div>
              <p className="text-sm font-semibold text-gray-800 leading-tight">Get notified</p>
              <p className="text-xs text-gray-500 leading-tight">We'll email you when this is back in stock</p>
            </div>
          </div>
          <button
            type="button"
            onClick={e => { e.preventDefault(); e.stopPropagation(); setShowEmail(false); }}
            className="shrink-0 mt-0.5 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleGuestSubmit} className="space-y-2">
          <input
            type="email"
            required
            autoFocus
            placeholder="your@email.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="w-full rounded-xl border-2 border-gray-200 px-4 py-2.5 text-sm bg-white focus:border-hsecondary focus:ring-4 focus:ring-hsecondary/10 outline-none transition-all duration-200 placeholder:text-gray-400"
          />
          <motion.button
            type="submit"
            whileTap={{ scale: 0.97 }}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 rounded-xl bg-hsecondary px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-hsecondary/90 hover:shadow-md transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Setting alert…
              </>
            ) : (
              <>
                <Bell className="h-4 w-4" />
                Notify me
              </>
            )}
          </motion.button>
        </form>
      </motion.div>
    );
  }

  /* -------------------------------------------------------------------------- */
  /*                               Default Button                               */
  /* -------------------------------------------------------------------------- */

  return (
    <motion.button
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      whileTap={{ scale: 0.97 }}
      onClick={handleSubscribeClick}
      disabled={loading}
      className={`${baseBtn} ${subscribeStyle} ${widthStyle} ${
        isSmall ? 'px-4 py-2 text-xs gap-1.5' : 'px-6 py-3 text-sm'
      } ${className}`}
    >
      {loading ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          {!isSmall && (
            <>
              <span className="sm:hidden">Setting…</span>
              <span className="hidden sm:inline">Setting alert…</span>
            </>
          )}
        </>
      ) : (
        <>
          <motion.span
            animate={{ rotate: [0, -8, 8, -4, 4, 0] }}
            transition={{ duration: 0.6, delay: 0.4, ease: 'easeInOut' }}
          >
            <Bell className={isSmall ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
          </motion.span>
          {isSmall ? (
            'Notify me'
          ) : (
            <>
              <span className="sm:hidden">Notify me</span>
              <span className="hidden sm:inline">Notify When In Stock</span>
            </>
          )}
        </>
      )}
    </motion.button>
  );
}
